import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { ChartContainer } from '../RadarChart/styles';
import { FaExpand, FaCompress } from 'react-icons/fa';
import ZoomControls from '../ZoomControls';
import {
    APPROVAL_STAGE_COLORS,
    APPROVAL_STAGE_RADII,
    COLORS,
    SEGMENT_TYPE,
} from '../../../../constants/radar';

const QuadrantRadarChart = ({
  data,
  hoveredItem,
  onHover,
  onMouseOut,
  zoomFactor,
}) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [focusedQuadrant, setFocusedQuadrant] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [containerDimensions, setContainerDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    
    const updateDimensions = () => {
      if (!container) return;
      
      const bounds = container.getBoundingClientRect();
      setContainerDimensions({
        width: bounds.width,
        height: bounds.height
      });
    };

    updateDimensions();

    const observer = new ResizeObserver(updateDimensions);
    
    observer.observe(container);

    return () => {
      if (container) {
        observer.unobserve(container);
      }
      observer.disconnect();
    };
  }, []);

  const getQuadrantTransform = (quadrantIndex) => {
    if (quadrantIndex === null) {
      return `scale(${zoomFactor})`;
    }
    
    const baseTranslationX = containerDimensions.width / 1.5;
    const baseTranslationY = containerDimensions.height / 1.5;
    
    const translationScale = 1 / zoomFactor;
    const translationValueX = baseTranslationX * translationScale;
    const translationValueY = baseTranslationY * translationScale;
    
    let translateX, translateY;
    switch (quadrantIndex) {
      case 0:
        translateX = translationValueX;
        translateY = translationValueY;
        break;
      case 1:
        translateX = -translationValueX;
        translateY = translationValueY;
        break;
      case 2:
        translateX = -translationValueX;
        translateY = -translationValueY;
        break;
      case 3:
        translateX = translationValueX;
        translateY = -translationValueY;
        break;
    }

    const focusScale = zoomFactor * 2;
    return `scale(${focusScale}) translate(${translateX},${translateY})`;
  };

  const handleQuadrantFocus = (quadrantIndex) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    
    const svg = d3.select(svgRef.current);
    const mainGroup = svg.select('g.main-group');
    const isUnfocusing = focusedQuadrant === quadrantIndex;
    const newFocusedQuadrant = isUnfocusing ? null : quadrantIndex;

    // Clear any existing transitions
    mainGroup.selectAll('.quadrant-group').interrupt();

    // Handle the main group transformation
    mainGroup
      .transition()
      .duration(500)
      .ease(d3.easeQuadInOut)
      .attr('transform', getQuadrantTransform(newFocusedQuadrant))
      .on('start', () => {
        // Update quadrant visibility
        data.subcategories?.forEach((_, idx) => {
          const quadrantElement = svg.select(`.quadrant-${idx}`);
          if (!quadrantElement.empty()) {
            quadrantElement
              .transition()
              .duration(500)
              .style('opacity', isUnfocusing ? 1 : (idx === quadrantIndex ? 1 : 0))
              .style('pointer-events', isUnfocusing ? 'all' : (idx === quadrantIndex ? 'all' : 'none'));
          }
        });

        // Update button text
        const buttonText = svg.select(`.quadrant-${quadrantIndex} .focus-button-text`);
        buttonText.text(isUnfocusing ? '+' : '−');
      })
      .on('end', () => {
        // Ensure all quadrants are visible when unfocusing
        if (isUnfocusing) {
          data.subcategories?.forEach((_, idx) => {
            const quadrantElement = svg.select(`.quadrant-${idx}`);
            if (!quadrantElement.empty()) {
              quadrantElement
                .style('opacity', 1)
                .style('pointer-events', 'all')
                // Make sure all elements within the quadrant are visible
                .selectAll('*')
                .style('opacity', 1)
                .style('pointer-events', 'all');
            }
          });
        }
        setFocusedQuadrant(newFocusedQuadrant);
        setIsTransitioning(false);
      });
  };

  useEffect(() => {
    if (!data || !svgRef.current) return;

    const svg = d3.select(svgRef.current)
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', '-1200 -1200 2400 2400')
      .attr('preserveAspectRatio', 'xMidYMid meet');

    let mainGroup = svg.select('g.main-group');
    if (mainGroup.empty()) {
      svg.selectAll('*').remove();
      mainGroup = svg.append('g').attr('class', 'main-group');
    }

    // Clear existing approval stages and quadrant lines
    mainGroup.selectAll('circle').remove();
    mainGroup.selectAll('path').remove();
    mainGroup.selectAll('line').remove();
    mainGroup.selectAll('text:not(.focus-button-text)').remove(); // Keep button text

    mainGroup
      .transition()
      .duration(250)
      .ease(d3.easeQuadInOut)
      .attr('transform', getQuadrantTransform(focusedQuadrant));

    const rScale = d3.scaleLinear().domain([0, 10]).range([0, 720]);

    // Add approval stages circles and labels
    const approvalStages = [
      { name: 'ADOPT', radius: APPROVAL_STAGE_RADII['ADOPT'] },
      { name: 'TRIAL', radius: APPROVAL_STAGE_RADII['TRIAL'] },
      { name: 'ASSESS', radius: APPROVAL_STAGE_RADII['ASSESS'] },
      { name: 'HOLD', radius: APPROVAL_STAGE_RADII['HOLD'] }
    ];

    approvalStages.forEach(stage => {
      if (focusedQuadrant === null) {
        mainGroup
          .append('circle')
          .attr('r', rScale(stage.radius))
          .style('fill', 'none')
          .style('stroke', COLORS.BACKGROUND)
          .style('stroke-width', '1px');

        mainGroup
          .append('text')
          .attr('x', 0)
          .attr('y', -rScale(stage.radius) + 20)
          .attr('text-anchor', 'middle')
          .style('font-size', '14px')
          .style('fill', APPROVAL_STAGE_COLORS[stage.name])
          .style('opacity', 0.6)
          .style('font-weight', 'bold')
          .text(stage.name);
      } else {
        const getArcAngles = (quadrantIndex) => {
          switch (quadrantIndex) {
            case 0:
              return { startAngle: Math.PI * 3/2, endAngle: Math.PI * 2 };
            case 1:
              return { startAngle: 0, endAngle: Math.PI/2 };
            case 2:
              return { startAngle: Math.PI/2, endAngle: Math.PI };
            case 3:
              return { startAngle: Math.PI, endAngle: Math.PI * 3/2 };
            default:
              return { startAngle: 0, endAngle: Math.PI * 2 };
          }
        };

        const { startAngle, endAngle } = getArcAngles(focusedQuadrant);

        const arc = d3.arc()
          .innerRadius(0)
          .outerRadius(rScale(stage.radius))
          .startAngle(startAngle)
          .endAngle(endAngle);

        mainGroup
          .append('path')
          .attr('d', arc())
          .style('fill', 'none')
          .style('stroke', COLORS.BACKGROUND)
          .style('stroke-width', '1px');

        const getLabelPosition = (quadrantIndex, radius, stageIndex, totalStages) => {
          const y = quadrantIndex < 2 ? -radius : radius;
          const xOffset = 20;
          const x = quadrantIndex === 0 || quadrantIndex === 3 ? xOffset : -xOffset;
          
          return {
            x: x,
            y: y,
            anchor: quadrantIndex === 0 || quadrantIndex === 3 ? 'end' : 'start'
          };
        };

        const { x, y, anchor } = getLabelPosition(
          focusedQuadrant,
          rScale(stage.radius) - 20,
          approvalStages.indexOf(stage),
          approvalStages.length
        );

        mainGroup
          .append('text')
          .attr('x', x)
          .attr('y', y)
          .attr('text-anchor', anchor)
          .style('font-size', '16px')
          .style('fill', APPROVAL_STAGE_COLORS[stage.name])
          .style('opacity', 0.6)
          .style('font-weight', 'bold')
          .text(stage.name);
      }
    });

    // Add quadrant lines when not focused
    if (focusedQuadrant === null) {
      const quadrantLines = [
        { angle: 0 },
        { angle: 90 }
      ];

      quadrantLines.forEach(line => {
        mainGroup
          .append('line')
          .attr('x1', 0)
          .attr('y1', -rScale(8.5))
          .attr('x2', 0)
          .attr('y2', rScale(8.5))
          .attr('transform', `rotate(${line.angle})`)
          .style('stroke', COLORS.BACKGROUND)
          .style('stroke-width', '2px');
      });
    }

    // Draw or update the quadrant groups
    const quadrantGroups = mainGroup.selectAll('.quadrant-group')
      .data(data.subcategories || [], d => d.name);

    // Remove old groups
    quadrantGroups.exit().remove();

    // Create new groups
    const quadrantEnter = quadrantGroups.enter()
      .append('g')
      .attr('class', (d, i) => `quadrant-group quadrant-${i}`);

    // Update all groups (new and existing)
    quadrantGroups.merge(quadrantEnter)
      .transition()
      .duration(500)
      .style('opacity', d => (focusedQuadrant === null || focusedQuadrant === data.subcategories.indexOf(d)) ? 1 : 0)
      .style('pointer-events', d => (focusedQuadrant === null || focusedQuadrant === data.subcategories.indexOf(d)) ? 'all' : 'none');

    // Rest of your drawing code...
    let globalNodeIndex = 1;

    // Draw the elements for each quadrant
    quadrantGroups.merge(quadrantEnter).each(function(quadrant, index) {
      const quadrantGroup = d3.select(this);
      
      // Clear existing content
      quadrantGroup.selectAll('*').remove();

      const getLeafNodes = (node) => {
        if (node.segmentType === SEGMENT_TYPE.LEAF) return [node];
        return node.subcategories?.flatMap(getLeafNodes) || [];
      };

      const leafNodes = getLeafNodes(quadrant);

      const nodesByStage = leafNodes.reduce((acc, node) => {
        const stage = node.approvalStage.toUpperCase();
        if (!acc[stage]) acc[stage] = [];
        acc[stage].push(node);
        return acc;
      }, {});

      const seededRandom = (seed) => {
        const x = Math.sin(seed++) * 10000;
        return x - Math.floor(x);
      };

      const isTooClose = (x1, y1, points, minDistance = 30) => {
        for (const point of points) {
          const dx = x1 - point.x;
          const dy = y1 - point.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          if (distance < minDistance) return true;
        }
        return false;
      };

      const defs = svg.append('defs');
      
      const style = defs.append('style')
        .text(`
          @keyframes pulse {
            0% { filter: url(#glow) opacity(0.3); }
            50% { filter: url(#glow) opacity(1); }
            100% { filter: url(#glow) opacity(0.3); }
          }
          .pulse-glow {
            animation: pulse 1.5s ease-in-out infinite;
          }
        `);
      
      const filter = defs.append('filter')
        .attr('id', 'glow')
        .attr('x', '-50%')
        .attr('y', '-50%')
        .attr('width', '200%')
        .attr('height', '200%');

      filter.append('feGaussianBlur')
        .attr('stdDeviation', '3')
        .attr('result', 'coloredBlur');

      const feMerge = filter.append('feMerge');
      feMerge.append('feMergeNode')
        .attr('in', 'coloredBlur');
      feMerge.append('feMergeNode')
        .attr('in', 'SourceGraphic');

      // Create nodeIndices map at this level
      const nodeIndices = new Map();
      
      // Pre-populate nodeIndices with all nodes using globalNodeIndex
      Object.values(nodesByStage).forEach(nodes => {
        nodes.forEach(node => {
          nodeIndices.set(node.name, globalNodeIndex++);
        });
      });

      Object.entries(nodesByStage).forEach(([stage, nodes]) => {
        const stageRadius = APPROVAL_STAGE_RADII[stage];
        const nextStageRadius = stage === 'ADOPT' ? 0 : 
          stage === 'TRIAL' ? APPROVAL_STAGE_RADII['ADOPT'] :
          stage === 'ASSESS' ? APPROVAL_STAGE_RADII['TRIAL'] :
          APPROVAL_STAGE_RADII['ASSESS'];
        
        const placedPoints = [];

        nodes.forEach((node, idx) => {
          const seed = node.name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
          
          const quadrantStart = {
            0: 185,
            1: -85,
            2: 5,
            3: 95,
          }[index];

          const quadrantSpread = 80;

          let x, y;
          let attempts = 0;
          const maxAttempts = 50;

          do {
            const randomAngle = quadrantStart + (seededRandom(seed + attempts) * quadrantSpread) + (seededRandom(seed + attempts + 2) * 5 - 2.5);
            const nodeRad = (randomAngle * Math.PI) / 180;

            const minRadius = rScale(nextStageRadius);
            const maxRadius = rScale(stageRadius);
            const randomRadius = minRadius + (seededRandom(seed + attempts + 1) * (maxRadius - minRadius)) + (seededRandom(seed + attempts + 3) * 10 - 5);

            x = randomRadius * Math.cos(nodeRad);
            y = randomRadius * Math.sin(nodeRad);
            attempts++;
          } while (isTooClose(x, y, placedPoints) && attempts < maxAttempts);

          placedPoints.push({ x, y });

          // Use the pre-populated index
          const currentIndex = nodeIndices.get(node.name);

          quadrantGroup
            .append('circle')
            .attr('cx', x)
            .attr('cy', y)
            .attr('r', 5)
            .attr('class', `node-${currentIndex}`)
            .style('fill', APPROVAL_STAGE_COLORS[stage])
            .style('cursor', 'pointer')
            .style('transition', 'all 0.3s ease-in-out')
            .on('mouseover', (event) => {
              d3.select(`.node-${currentIndex}`)
                .attr('r', 8)
                .style('filter', 'url(#glow)')
                .classed('pulse-glow', true);
              d3.select(`.legend-${currentIndex}`).style('font-weight', 'bold');
              onHover(node, event);
            })
            .on('mouseout', (event) => {
              d3.select(`.node-${currentIndex}`)
                .attr('r', 5)
                .style('filter', 'none')
                .classed('pulse-glow', false);
              d3.select(`.legend-${currentIndex}`).style('font-weight', 'normal');
              onMouseOut(event);
            });

          quadrantGroup
            .append('text')
            .attr('x', x + 10)
            .attr('y', y + 5)
            .style('font-size', '12px')
            .style('fill', COLORS.SECONDARY)
            .text(currentIndex);
        });
      });

      // Add legend
      const legendItems = [];

      ['ADOPT', 'TRIAL', 'ASSESS', 'HOLD'].forEach(stage => {
        if (nodesByStage[stage] && nodesByStage[stage].length > 0) {
          legendItems.push({
            isHeader: true,
            name: stage,
            stage: stage
          });

          nodesByStage[stage].forEach(node => {
            // Use the pre-populated index
            const currentIndex = nodeIndices.get(node.name);
            legendItems.push({
              index: currentIndex,
              name: node.name,
              stage: stage
            });
          });
        }
      });

      const maxRadius = rScale(APPROVAL_STAGE_RADII['HOLD']);
      const MAX_COLUMN_HEIGHT = maxRadius;
      const ITEM_HEIGHT = 25;
      const COLUMN_WIDTH = 300;

      const maxItemsPerColumn = Math.floor(MAX_COLUMN_HEIGHT / ITEM_HEIGHT);
      const numColumns = Math.ceil(legendItems.length / maxItemsPerColumn);

      const listingX = index === 0 ? -900 : index === 1 ? 900 : index === 2 ? 700 : -900;
      const listingBaseY = index === 0 ? -600 : index === 1 ? -600 : index === 2 ? 100 : 100;

      let currentYPos = listingBaseY + 50;
      let currentColumn = 0;

      legendItems.forEach((item, idx) => {
        if (item.isHeader) {
          if (currentYPos + ITEM_HEIGHT > listingBaseY + maxItemsPerColumn * ITEM_HEIGHT) {
            currentColumn++;
            currentYPos = listingBaseY + 50;
          }
        }

        const columnOffset = currentColumn * COLUMN_WIDTH;
        const xPos = index === 2 || index === 3 
          ? listingX - columnOffset 
          : listingX + columnOffset;

        quadrantGroup
          .append('text')
          .attr('x', xPos)
          .attr('y', currentYPos)
          .attr('class', item.isHeader ? '' : `legend-${item.index}`)
          .attr('text-anchor', index === 2 || index === 3 ? 'end' : 'start')
          .style('font-size', item.isHeader ? '20px' : '16px')
          .style('font-weight', item.isHeader ? 'bold' : 'normal')
          .style('fill', item.isHeader ? APPROVAL_STAGE_COLORS[item.stage] : COLORS.PRIMARY)
          .text(item.isHeader ? item.name : `${item.index}. ${item.name}`)
          .style('cursor', item.isHeader ? 'default' : 'pointer')
          .style('transition', 'all 0.3s ease-in-out')
          .on('mouseover', (event) => {
            if (!item.isHeader) {
              d3.select(`.legend-${item.index}`)
                .style('font-weight', 'bold')
                .style('font-size', '20px')
                .style('fill', COLORS.PRIMARY);

              d3.select(`.node-${item.index}`)
                .attr('r', 10)
                .style('filter', 'url(#glow)')
                .classed('pulse-glow', true);

              if (onHover) {
                const nodeData = nodesByStage[item.stage].find(node => 
                  node.name === item.name
                );
                onHover(nodeData, event);
              }
            }
          })
          .on('mouseout', (event) => {
            if (!item.isHeader) {
              d3.select(`.legend-${item.index}`)
                .style('font-weight', 'normal')
                .style('font-size', '16px')
                .style('fill', COLORS.PRIMARY);

              d3.select(`.node-${item.index}`)
                .attr('r', 5)
                .style('filter', 'none')
                .classed('pulse-glow', false);

              if (onMouseOut) {
                onMouseOut(event);
              }
            }
          });

        currentYPos += ITEM_HEIGHT;
      });

      const quadrantTitle = quadrantGroup
        .append('text')
        .attr('x', listingX)
        .attr('y', listingBaseY)
        .attr('text-anchor', index === 2 || index === 3 ? 'end' : 'start')
        .style('font-size', '28px')
        .style('font-weight', 'bold')
        .style('fill', COLORS.PRIMARY)
        .text(quadrant.name);

      const buttonX = listingX + (index === 2 || index === 3 ? 40 : -40);
      const buttonY = listingBaseY;

      const buttonContainer = quadrantGroup
        .append('g')
        .attr('class', 'button-container')
        .attr('transform', `translate(${buttonX}, ${buttonY})`);

      const updateButtonText = () => {
        quadrantGroup
          .select('.focus-button-text')
          .text(index === focusedQuadrant ? '−' : '+');
      };

      const buttonGroup = buttonContainer
        .append('g')
        .attr('class', 'focus-button')
        .style('cursor', 'pointer')
        .style('pointer-events', isTransitioning ? 'none' : 'all')
        .on('click', (event) => {
          event.stopPropagation();
          if (!isTransitioning) {
            handleQuadrantFocus(index);
            updateButtonText();
          }
        });

      buttonGroup
        .append('circle')
        .attr('cx', 0)
        .attr('cy', 0)
        .attr('r', 15)
        .style('fill', COLORS.SECONDARY)
        .style('opacity', isTransitioning ? 0.5 : 0.8);

      buttonGroup
        .append('text')
        .attr('class', 'focus-button-text')
        .attr('x', 0)
        .attr('y', 0)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .style('fill', 'white')
        .style('font-family', 'FontAwesome')
        .style('font-size', '14px')
        .style('opacity', isTransitioning ? 0.5 : 1)
        .text(index === focusedQuadrant ? '−' : '+');

      buttonGroup
        .on('mouseenter', function() {
          if (!isTransitioning) {
            d3.select(this)
              .transition()
              .duration(200)
              .attr('transform', 'scale(1.1)')
              .style('opacity', 1);
          }
        })
        .on('mouseleave', function() {
          d3.select(this)
            .transition()
            .duration(200)
            .attr('transform', 'scale(1)')
            .style('opacity', isTransitioning ? 0.5 : 0.8);
        });
    });
  }, [data, focusedQuadrant, zoomFactor, containerDimensions]);

  return (
    <ChartContainer
      ref={containerRef}
      style={{
        cursor: 'default'
      }}
    >
      <svg ref={svgRef} />
    </ChartContainer>
  );
};

export default QuadrantRadarChart;
